"use client";

import { useCallback, useState } from "react";
import type { SpecialOffer, OfferDetail } from "@/lib/offers";
import { getOfferRelations, fetchOfferByPageUrlClient, offerText } from "@/lib/offers";
import Dialog from "@/app/(main)/components/Dialog";
import OfferFlyerBar from "@/app/(main)/components/OfferFlyerBar";
import OfferRelations from "@/app/(main)/components/OfferRelations";
import { extractStandaloneLinks } from "@/lib/htmlUtils";
import styles from "./special-offer-card.module.css";

const FALLBACK_PHOTO = "/main/img/offer/offer1.jpg";

// Shared inline resets for the image/title trigger buttons (unstyled <button>).
const IMG_BTN: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  lineHeight: 0,
};
const TITLE_BTN: React.CSSProperties = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "none",
  font: "inherit",
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
};

export default function SpecialOfferCard({ offer }: { offer: SpecialOffer }) {
  // Relations derived from the list-view payload — used on the card and as a
  // fast first-paint inside the dialog until the detail payload arrives.
  const cardRelations = getOfferRelations(offer);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openDialog = useCallback(async () => {
    setOpen(true);

    if (detail) return;

    if (!offer.pageUrl) {
      setError("This offer has no further details.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchOfferByPageUrlClient(offer.pageUrl);
      if (data) setDetail(data);
      else setError("Offer details are not available right now.");
    } catch {
      setError("We couldn't load the offer details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [detail, offer.pageUrl]);

  // While loading, fall back to the relations we already have from the list.
  const dialogRelations = detail ? getOfferRelations(detail) : cardRelations;
  const subTitle = offerText(offer.subTitle);
  const photo = offer.photo?.trim() || FALLBACK_PHOTO;
  const teaserLabelId = `offer-${offer.id}-teaser`;

  // Pull standalone CTA links out of the short description so they can sit next
  // to the "View Offer" button; `descHtml` is the remaining copy, rendered full.
  const { html: descHtml, links: ctaLinks } = extractStandaloneLinks(
    offer.shortDescription,
  );
  // CMS-authored deal flag (e.g. "SAVE 10%"), shown over the image when set.
  const badge = offerText(offer.offerBadge);

  // Dialog content sources — short description first, full description second.
  const dlgShort = (detail?.shortDescription || offer.shortDescription || "").trim();
  const dlgFull = (detail?.description || "").trim();
  // Terms come from the detail payload; the list row carries them too, so fall
  // back to it and the section renders on first paint instead of popping in.
  const dlgTerms = offerText(detail?.terms) ?? offerText(offer.terms);
  const flyer = detail?.downloadFile?.file?.trim();

  return (
    <>
      <div className={`tour-box th-ani gsap-cursor h-100 ${styles.card}`}>
        <div className={`tour-box_img global-img ${styles.imgWrap}`}>
          <button
            type="button"
            onClick={openDialog}
            aria-label={offer.title}
            style={IMG_BTN}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={photo} alt={offer.title} />
          </button>
          {badge && (
            <span className={styles.saveBadge}>
              <i className="fa-solid fa-tag" aria-hidden="true" />
              {badge}
            </span>
          )}
        </div>

        <div className={`tour-content ${styles.content}`}>
          <h3 className="box-title fw-bolder mb-0">
            <button type="button" onClick={openDialog} style={TITLE_BTN}>
              {offer.title}
            </button>
          </h3>
          {subTitle && <p className={styles.subTitle}>{subTitle}</p>}

          {/* ── The offer (rich short description, rendered in full) ── */}
          {descHtml && (
            <>
              <p className={styles.eyebrow} id={teaserLabelId}>
                <i className="fa-solid fa-tag" aria-hidden="true" /> The Offer
              </p>
              <div
                className={styles.teaser}
                aria-labelledby={teaserLabelId}
                dangerouslySetInnerHTML={{ __html: descHtml }}
              />
            </>
          )}

          {/* ── Related packages / destinations / regions (below teaser) ── */}
          {cardRelations.length > 0 && (
            <>
              <p className={styles.eyebrow}>
                <i className="fa-solid fa-layer-group" aria-hidden="true" />{" "}
                Included in this offer
              </p>
              <OfferRelations relations={cardRelations} variant="card" />
            </>
          )}

          {/* ── Primary action + any extracted CTA links, pinned to bottom ── */}
          <div className={`tour-action justify-content-start ${styles.ctaRow}`}>
            <button
              type="button"
              onClick={openDialog}
              className={styles.ctaPrimary}
            >
              View Offer
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
            {ctaLinks.map((link) => (
              <a
                key={link.href}
                className={styles.ctaLink}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={link.label}
              >
                {link.label}
                <i
                  className="fa-solid fa-arrow-up-right-from-square"
                  aria-hidden="true"
                />
              </a>
            ))}
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={offer.title}
        subtitle={subTitle}
        toolbar={
          flyer ? (
            <OfferFlyerBar file={flyer} title={detail?.downloadFile?.downloadTitle} />
          ) : undefined
        }
        loading={loading}
        width={760}
        maxWidth="min(92vw, 860px)"
        closeOnBackdropClick={false}
        footer={
          <button
            type="button"
            className={styles.dialogCloseBtn}
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        }
      >
        {error ? (
          <p className="blog-text mb-0">{error}</p>
        ) : (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className={styles.heroImg} src={photo} alt={offer.title} />

            {/* The Offer — short description first */}
            {dlgShort && (
              <section
                className={styles.offerPanel}
                aria-labelledby={`dlg-offer-${offer.id}`}
              >
                <p className={styles.eyebrow} id={`dlg-offer-${offer.id}`}>
                  <i className="fa-solid fa-tag" aria-hidden="true" /> The Offer
                </p>
                <div
                  className={styles.richHtml}
                  dangerouslySetInnerHTML={{ __html: dlgShort }}
                />
              </section>
            )}

            {/* Details — full description below, only when present */}
            {dlgFull && (
              <>
                <hr className={styles.divider} />
                <section aria-labelledby={`dlg-details-${offer.id}`}>
                  <p className={styles.eyebrow} id={`dlg-details-${offer.id}`}>
                    <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
                    Details
                  </p>
                  <div
                    className={styles.richHtml}
                    dangerouslySetInnerHTML={{ __html: dlgFull }}
                  />
                </section>
              </>
            )}

            {/* Terms & conditions — the CMS copy carries its own heading, so
                this renders the HTML as-is rather than adding a duplicate. */}
            {dlgTerms && (
              <>
                <hr className={styles.divider} />
                <div
                  className={`${styles.richHtml} ${styles.terms}`}
                  dangerouslySetInnerHTML={{ __html: dlgTerms }}
                />
              </>
            )}

            {dialogRelations.length > 0 && (
              <div className="mt-3">
                <OfferRelations relations={dialogRelations} variant="full" />
              </div>
            )}

            {!dlgShort && !dlgFull && !dlgTerms && !flyer && (
              <p className="blog-text mb-0">
                No details available for this offer.
              </p>
            )}
          </>
        )}
      </Dialog>
    </>
  );
}
